import { TenantViewModel } from '@ail/customers-api'
import { DatabaseReportViewModel, DocumentReportViewModel, ReportViewModel } from '@ail/reports-api'
import React, { Component } from 'react'
import { WithTranslation, withTranslation } from 'react-i18next'
import { ConnectedProps, connect } from 'react-redux'
import { NavLink } from 'react-router-dom'
import { downloadFile } from '../../helpers/FileDownload'
import { Matomo } from '../../helpers/Matomo'
import { printDate } from '../../helpers/Utils'
import { wrapQuery } from '../../redux/api/apiQuery'
import { customersApi } from '../../redux/api/customerApi'
import { isDatabaseReport, isDocumentReport, reportsApi } from '../../redux/api/reportsApi'
import { State } from '../../redux/reducers/initialState'
import { Reports } from '../../utils/reports'
import DataSecurityPopup from '../ContractDetails/Popups/DataSecurityPopup'
import Alert from '../common/Alert/Alert'
import './ReportsComponent.scss'
import { Loader } from '../common/Loader/Loader'

const withoutOwner = 'withoutOwner'

type IProps = ConnectedProps<typeof connector> & WithTranslation

interface IState {
  loading: boolean
  currentCompany: string
  reportsTenants: TenantViewModel[]
  invalidOwners: string[]
  availableReports: ReportViewModel[]
  availableReportsToRender: ReportViewModel[]
  showWarning: boolean
  showInternal: boolean
  pendingExportConfirm?: { onConfirm: () => void }
}

class AvailableReports extends Component<IProps, IState> {
  constructor(props: IProps) {
    super(props)

    this.state = {
      loading: false,
      currentCompany: 'selectCompany',
      reportsTenants: [],
      invalidOwners: [],
      availableReports: [],
      availableReportsToRender: [],
      showWarning: false,
      showInternal: false,
    }
  }

  private onCompanyDdChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const currentCompany = e.target.value

    this.setState({
      currentCompany,
    })
  }

  componentDidMount() {
    if (this.props.currentUser?.isInternalUser) {
      this.props.loadTenants().then((tenants) => {
        this.loadAvailableReports(tenants)
      })
    } else {
      this.loadAvailableReports([])
    }
  }

  componentDidUpdate(oldProps: IProps, oldState: IState) {
    const { currentCompany, availableReports } = this.state

    if (availableReports !== oldState.availableReports || currentCompany !== oldState.currentCompany) {
      this.updateAvailableReportsToRender()
    }
  }

  private updateAvailableReportsToRender() {
    const { currentCompany, invalidOwners } = this.state
    let showInternal = false
    const { availableReports } = this.state
    const standardFiltering = currentCompany !== withoutOwner
    const availableReportsToRender = currentCompany
      ? availableReports.filter((r) =>
          standardFiltering ? r.tenantId === currentCompany : invalidOwners.includes(r.id)
        )
      : availableReports

    if (availableReportsToRender.some((r) => this.isReportPrivateOrInternal(r.visibility))) {
      showInternal = true
    }

    this.setState({
      availableReportsToRender,
      showInternal,
    })
  }

  private prepareCustomersAndTenantsDd(availableReports: ReportViewModel[], tenants: TenantViewModel[]) {
    const reportsTenants = tenants
      .filter((t) => availableReports.some((r) => r.tenantId === t.id))
      .sort((a, b) => a.name.localeCompare(b.name))
    const invalidTenants = availableReports
      .filter((r) => !r.tenantId || !tenants.find((t) => t.id === r.tenantId))
      .map((r) => r.id)
    const invalidOwners = invalidTenants

    if (reportsTenants.length <= 1) {
      this.setState({
        currentCompany: '',
      })
    }

    this.setState({
      reportsTenants,
      invalidOwners,
    })
  }

  private loadAvailableReports(tenants: TenantViewModel[]) {
    this.setState({
      loading: true,
    })

    this.props.getAvailableReports().then(
      (reports) => {
        this.setState({ availableReports: reports })
        let showInternal = false

        if (Array.isArray(reports)) {
          showInternal = reports.some((r) => this.isReportPrivateOrInternal(r.visibility))
        }

        this.prepareCustomersAndTenantsDd(reports, tenants)

        this.setState({
          loading: false,
          showInternal,
        })
      },
      () => {
        this.setState({
          loading: false,
        })
      }
    )
  }

  /**
   * Report is private or internal.
   * @param visibility
   * @returns
   */
  private isReportPrivateOrInternal(visibility: string): boolean {
    return visibility === 'Private' || visibility === 'Internal'
  }

  private hasInternalOrPrivateReports() {
    return this.state.availableReportsToRender.some((r) => r.visibility === 'Private' || r.visibility === 'Internal')
  }

  /**
   * Download the report.
   * @param report selected report
   */
  private download(report: ReportViewModel) {
    if (!report) return
    Matomo.pushEvent('DataProtection', 'Export', 'Report', report?.id)

    this.setState({
      loading: true,
    })

    this.props.getReport(report.id, false).then(
      (report) => {
        if (isDocumentReport(report) && report.fileName) {
          this.downloadFileReport(report)
        } else if (isDatabaseReport(report)) {
          this.exportWYSIWYG(report)
        } else {
          this.setState({ showWarning: true })
        }

        this.setState({
          loading: false,
        })
      },
      () => {
        this.setState({
          showWarning: true,
          loading: false,
        })
      }
    )
  }

  /**
   * Export report as Excel.
   */
  private exportWYSIWYG(report: DatabaseReportViewModel) {
    Reports.exportDatabaseReport(report)
  }

  /**
   * Download report as file.
   */
  private downloadFileReport(report: DocumentReportViewModel) {
    this.setState({
      loading: true,
    })

    const a = document.createElement('a')
    document.body.appendChild(a)

    const byteCharacters = atob(report?.data as string)
    const byteNumbers = new Array(byteCharacters.length)
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i)
    }

    const fileURL = URL.createObjectURL(new Blob([new Uint8Array(byteNumbers)], { type: report.mimeType }))
    const filename = report?.fileName || report?.name

    downloadFile({ fileURL, filename }, () => {
      this.setState({ loading: false })
    })
  }

  private toggleInternalReportsVisibility(visible?: boolean) {
    if (!visible) {
      visible = !this.state.showInternal
    }

    this.setState({
      showInternal: visible,
    })
  }

  private filterIsVisible(): boolean {
    return this.state.reportsTenants.length > 1
  }

  render() {
    const { loading, reportsTenants, invalidOwners, availableReportsToRender } = this.state
    const { t, currentUser } = this.props

    return (
      <div className="reports-component">
        <Loader blocking={loading} style={{ overflow: 'hidden' }}>
          <>
            <div className="contractFilters reportFilters">
              <div className="leftContainer">
                <div className="reqTitle">
                  <i className="icon-report"></i>
                  <span>{t('AVAILABLE_REPORTS')}</span>
                </div>
              </div>
              <div className="rightFilters">
                {this.filterIsVisible() && (
                  <span className="reports-filter-by-customer">
                    <label>{t('FILTER_REPORTS_BY_CUSTOMER')}</label>{' '}
                    <div className="selectArea">
                      <select value={this.state.currentCompany} onChange={(e) => this.onCompanyDdChange(e)}>
                        {currentUser?.isInternalUser && this.state.currentCompany === 'selectCompany' && (
                          <option value="selectCompany">{t('COMPANY')}</option>
                        )}

                        {reportsTenants.map((rt, i) => (
                          <option key={i} value={rt.id}>
                            {rt.name}
                          </option>
                        ))}
                        {invalidOwners.length > 0 && <option value={withoutOwner}>{t('WITHOUT_OWNER')}</option>}
                      </select>
                      <i className="icon-bottom" />
                    </div>
                    {this.hasInternalOrPrivateReports() && (
                      <span className="eyeOk" onClick={() => this.toggleInternalReportsVisibility()}>
                        <i className={this.state.showInternal ? 'icon-show' : 'icon-hide'}></i>
                      </span>
                    )}
                  </span>
                )}
              </div>
            </div>
            {this.state.currentCompany !== 'selectCompany' && (
              <div className="row">
                <div className="col-12 col-sm-6 col-md-6 col-lg-6">
                  <div className="reportAreaList">
                    <div className={['detailTitle', this.state.showInternal ? 'internal' : ''].join(' ')}>
                      <i className="icon-ondemand" />
                      <span>{t('REPORTS_ON_DEMAND')}</span>
                    </div>
                    <div className="detail-container__grid costs print">
                      <div className="p-datatable p-component">
                        <div className="p-datatable-wrapper">
                          <table role="grid">
                            <tbody className="p-datatable-tbody">
                              {availableReportsToRender
                                .filter((r) => !r.schedule)
                                .filter((r) =>
                                  this.isReportPrivateOrInternal(r.visibility) ? this.state.showInternal : true
                                )
                                .sort((a, b) => a.name.localeCompare(b.name))
                                .map((r, idx) => (
                                  <tr key={idx} className="p-datatable-row">
                                    <td className="type">
                                      <i className="icon-excel" />
                                    </td>
                                    <td className={this.isReportPrivateOrInternal(r.visibility) ? 'text-red' : ''}>
                                      {r.name}
                                    </td>
                                    <td>
                                      <a
                                        className="download-column"
                                        onClick={() =>
                                          this.setState({ pendingExportConfirm: { onConfirm: () => this.download(r) } })
                                        }
                                      >
                                        <i className="icon-download" />
                                      </a>
                                      {r.kind !== 'document' && (
                                        <>
                                          <NavLink className="download-column" to={`/reports/${r.id}`}>
                                            <i className="icon-show" />
                                          </NavLink>
                                          {currentUser?.isAdmin && (
                                            <NavLink
                                              title={t('FORCE_EXECUTE_REPORT')}
                                              className="download-column force"
                                              to={`/reports/${r.id}/force/true`}
                                            >
                                              <i className="icon-show" />
                                            </NavLink>
                                          )}
                                        </>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-12 col-sm-6 col-md-6 col-lg-6">
                  <div className="reportAreaList">
                    <div className={['detailTitle', this.state.showInternal ? 'internal' : ''].join(' ')}>
                      <i className="icon-clock" />
                      <span>{t('REPORTS_SCHEDULED')}</span>
                    </div>
                    <div className="detail-container__grid costs print">
                      <div className="p-datatable p-component">
                        <div className="p-datatable-wrapper">
                          <table role="grid">
                            <tbody className="p-datatable-tbody">
                              {availableReportsToRender
                                .filter(isDatabaseReport)
                                .filter((r) => r.schedule)
                                .filter((r) =>
                                  this.isReportPrivateOrInternal(r.visibility) ? this.state.showInternal : true
                                )

                                .sort((a, b) => a.name.localeCompare(b.name))

                                .map((r, idx) => (
                                  <tr key={idx} className="p-datatable-row">
                                    <td className="type">
                                      <i className="icon-excel" />
                                    </td>
                                    <td className={this.isReportPrivateOrInternal(r.visibility) ? 'text-red' : ''}>
                                      {r.name}
                                    </td>
                                    <td>
                                      {r.lastRun ? (
                                        <>
                                          {printDate(r.lastRun, true).slice(0, -3)}
                                          <a
                                            className="download-column"
                                            onClick={() =>
                                              this.setState({
                                                pendingExportConfirm: { onConfirm: () => this.download(r) },
                                              })
                                            }
                                          >
                                            <i className="icon-download" />
                                          </a>
                                          {r.kind === 'database' && (
                                            <>
                                              <NavLink className="download-column" to={`/reports/${r.id}`}>
                                                <i className="icon-show" />
                                              </NavLink>
                                            </>
                                          )}
                                        </>
                                      ) : (
                                        <span className="pending">{t('PENDING')}</span>
                                      )}
                                      {r.kind === 'database' && currentUser?.isAdmin && (
                                        <NavLink
                                          title={t('FORCE_EXECUTE_REPORT')}
                                          className="download-column force"
                                          to={`/reports/${r.id}/force/true`}
                                        >
                                          <i className="icon-show" />
                                        </NavLink>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {this.state.currentCompany === 'selectCompany' && (
              <div className="select-company-text">{t('PLEASE_SELECT_A_COMPANY')}</div>
            )}
          </>
        </Loader>
        <Alert
          visible={this.state.showWarning}
          onClose={() => {
            this.setState({ showWarning: false })
          }}
          title={t('ERROR')}
          message={`An error occurred while loading the report.`}
        />
        <DataSecurityPopup
          onConfirm={() => {
            this.state.pendingExportConfirm?.onConfirm()
            this.setState({ pendingExportConfirm: undefined })
          }}
          onClose={() => this.setState({ pendingExportConfirm: undefined })}
          visible={!!this.state.pendingExportConfirm}
        />
      </div>
    )
  }
}
function mapStateToProps({ appData }: State) {
  return {
    currentUser: appData.currentUser,
  }
}

/**
 * What action should be passed to component
 */
const mapDispatchToProps = {
  getAvailableReports: wrapQuery(() => reportsApi.endpoints.getReports.initiate()),
  getReport: wrapQuery((reportId: string, force?: boolean) =>
    reportsApi.endpoints.getReport.initiate({ reportId, force })
  ),
  loadTenants: wrapQuery(() => customersApi.endpoints.getTenants.initiate()),
}

const connector = connect(mapStateToProps, mapDispatchToProps)
// eslint-disable-next-line react-refresh/only-export-components
export default connector(withTranslation('common')(AvailableReports))
